import { IntlShape } from 'react-intl';

import { BrandingLogo } from '@/components/SimplifiedSignup/Components/Branding';
import { FlowConfig } from '@/components/SimplifiedSignup/Context/types';
import { CampaignConfig } from '@/store/campaignConfig/types';

import { FlowScreenNames } from '../../types';
import { resolveLocaleContent } from './resolveLocaleContent';
import { generateCampaignScreenConfigs } from './Screens';
import { readPageConfig, resolveImageUrl, resolveTrialInfo, shapeCampaignForParams } from './types';

/**
 * Takes `formatMessage` (unlike `partnerConfigGenerator`) only to resolve the offer-summary
 * phrase up front into a plain string — that string is itself substituted as a placeholder
 * value into another message, not rendered as its own `MessageDescriptor`, so it can't be
 * deferred to render time the way `heading`/`subheading`/etc. are. Takes `locale` too, since
 * campaign copy is locale-keyed in a way partner copy never was.
 */
export const campaignConfigGenerator = (
	campaignConfig: CampaignConfig,
	locale: string,
	formatMessage: IntlShape['formatMessage'],
): FlowConfig => {
	const pageConfig = readPageConfig(campaignConfig.page_config);
	const content = resolveLocaleContent(pageConfig, locale);
	const trial = resolveTrialInfo(campaignConfig.trial_duration);
	const screens = generateCampaignScreenConfigs(
		campaignConfig,
		content,
		pageConfig.settings ?? {},
		trial,
		formatMessage,
	);
	const forParams = shapeCampaignForParams(campaignConfig, trial);

	// `page_config`'s partner-logo override takes precedence over the partner's API logo; explicit
	// `show: false` hides co-branding outright rather than falling back, since that's how an
	// admin says "no co-branding" for this campaign — everything else (unset, or a blank url)
	// falls back the same way.
	const coBrandedSrc =
		pageConfig.settings?.partner_logo?.show === false
			? null
			: resolveImageUrl(pageConfig.settings?.partner_logo) || campaignConfig.partner.logo_url;

	// Unlike partner-logo, there's no legacy fallback to prefer over — omitting `logo` here just
	// leaves `Branding`'s own default Calm mark in place, so every non-override case (unset,
	// blank, or explicitly hidden) resolves the same way.
	const productLogoUrl = resolveImageUrl(pageConfig.settings?.product_logo);
	const logo: BrandingLogo | undefined = productLogoUrl
		? { white: productLogoUrl, gradient: productLogoUrl, alt: 'Calm Logo' }
		: undefined;

	return {
		activeScreenKey: 'account' as FlowScreenNames,
		activeScreenConfig: screens.account,
		name: `campaign-${campaignConfig.slug}`,
		pageName: 'calm-partnerships-campaign-landing',
		screens,
		...(coBrandedSrc ? { coBranded: { src: coBrandedSrc, alt: `${campaignConfig.partner.name} Logo` } } : {}),
		...(logo ? { logo } : {}),
		...forParams,
		offerCountries: null,
		offerCurrencies: null,
		allowChurned: true,
	};
};
